import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const ManageOrganizers = () => {
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrganizer, setEditingOrganizer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    hotline: '',
    status: 1
  });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createData, setCreateData] = useState({
        name: '',
        email: '',
        max_events: '',
        commission: ''
    });
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');


    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3; // how many page numbers to show at once

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, last - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) {
            pageNumbers.push(i);
        }

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">

                    {/* Previous */}
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchOrganizers(current - 1)}>
                            Previous
                        </button>
                    </li>

                    {/* Page 1 + ellipsis */}
                    {start > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => fetchOrganizers(1)}>1</button>
                            </li>

                            {start > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}

                    {/* Dynamic Page Numbers */}
                    {pageNumbers.map((num) => (
                        <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchOrganizers(num)}>
                                {num}
                            </button>
                        </li>
                    ))}

                    {/* Ellipsis + Last Page */}
                    {end < last && (
                        <>
                            {end < last - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button className="page-link" onClick={() => fetchOrganizers(last)}>
                                    {last}
                                </button>
                            </li>
                        </>
                    )}

                    {/* Next */}
                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => fetchOrganizers(current + 1)}>
                            Next
                        </button>
                    </li>

                </ul>
            </nav>
        );
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 400); // ⏱️ debounce time (300–500ms is ideal)

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);


    useEffect(() => {
        fetchOrganizers(1);
    }, [debouncedSearch, statusFilter]);

    const fetchOrganizers = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const params = {
                page: pageNumber,
                search: debouncedSearch || undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined
            };

            const response = await api.get('/admin/organizers', { params });
            const paginator = response.data.data;

            setOrganizers(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
        } catch (error) {
            toast.error('Failed to load organizers');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (organizerId, currentStatus) => {
        const action = currentStatus === 1 ? 'Deactivate' : 'Activate';

        const result = await Swal.fire({
            title: `${action} Organizer`,
            text: `Are you sure you want to ${action.toLowerCase()} this organizer?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            const newStatus = currentStatus === 1 ? 0 : 1;
            await api.put(`/admin/organizers/${organizerId}/status`, {
                status: newStatus,
            });

            // Update toast message based on new status
            if (newStatus === 1) {
                toast.success('Organizer activated successfully');
            } else {
                toast.success('Organizer deactivated successfully');
            }

            fetchOrganizers();
        } catch (error) {
            toast.error('Failed to update organizer status');
        }
    };


    const handleDelete = async (organizerId) => {
        const result = await Swal.fire({
            title: 'Delete Organizer',
            text: 'Are you sure you want to delete this organizer? This will also delete all their events.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/admin/organizers/${organizerId}`);
            toast.success('Organizer deleted successfully');
            fetchOrganizers();
        } catch (error) {
            toast.error('Failed to delete organizer');
        }
    };

  const handleEdit = (organizer) => {
    setEditingOrganizer(organizer);
    setFormData({
      name: organizer.name || '',
      email: organizer.email || '',
      phone: organizer.phone || '',
      max_events: organizer.max_events || '',
        commission: organizer.commission || '',
      hotline: organizer.hotline || '',
      status: organizer.status
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingOrganizer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      max_events: '',
        Commission: '',
      hotline: '',
      status: 1
    });
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/organizers/${editingOrganizer.id}`, formData);
      toast.success('Organizer updated successfully');
      handleCloseModal();
      fetchOrganizers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update organizer');
    }
  };

    const handleCreateOrganizer = async (e) => {
        e.preventDefault();

        try {
            await api.post('/admin/organizers', createData);

            toast.success('Organizer created. Credentials email sent.');

            setShowCreateModal(false);
            setCreateData({
                name: '',
                email: '',
                max_events: '',
                commission: ''
            });

            fetchOrganizers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create organizer');
        }
    };


    const handleLoginAsOrganizer = async (organizerId) => {
    try {
      const response = await api.post('/admin/login-as-organizer', { organizer_id: organizerId });
      console.log('Login response:', response.data);
      if (response.data.success) {
        // Store current admin session for returning later
        const currentToken = localStorage.getItem('token');
        const currentUser = localStorage.getItem('user');
        localStorage.setItem('admin_return_token', currentToken);
        localStorage.setItem('admin_return_user', currentUser);
        localStorage.setItem('admin_impersonating', 'true');
        
        // Switch to organizer session
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.organizer));
        localStorage.setItem('userRole', 'organizer');
        
        toast.success('Logged in as organizer');
        // Redirect to organizer dashboard
        window.location.href = '/organizer/dashboard';
      }
    } catch (error) {
      toast.error('Failed to login as organizer');
      console.error('Error:', error);
    }
  };



  return (
    <div className="container-fluid">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0 font-weight-bold text-gray-900">Manage Organizers</h1>

            <button
                className="btn btn-sm btn-primary"
                onClick={() => setShowCreateModal(true)}
            >
                <i className="fas fa-plus mr-2"></i>
                Create Organizer
            </button>
        </div>

      {/* Filters */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Organizers Table */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-gray-900">All Organizers ({pagination.total || organizers.length})</h6>
        </div>
        <div className="card-body">
            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
          <div className="table-responsive">
            <table className="table table-bordered" width="100%" cellSpacing="0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Max Events</th>
                  <th>Commission</th>
                  <th>Events</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizers.length > 0 ? (
                  organizers.map(organizer => (
                    <tr key={organizer.id}>
                      <td>{organizer.id}</td>
                      <td>{organizer.name}</td>
                      <td>{organizer.email}</td>
                      <td>{organizer.phone || 'N/A'}</td>
                      <td>{organizer.max_events || 'N/A'}</td>
                      <td>{organizer.commission || 'N/A'}</td>
                      <td>
                        <span className="badge badge-primary">
                          {organizer.events_count || 0} events
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${organizer.status === 1 ? 'badge-success' : 'badge-danger'}`}>
                          {organizer.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{new Date(organizer.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            onClick={() => handleLoginAsOrganizer(organizer.id)}
                            className="p-2 text-primary rounded transition-colors border-0 bg-transparent"
                            style={{ background: 'transparent' }}
                            title="Login as Organizer"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#cfe2ff'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEdit(organizer)}
                            className="p-2 text-info rounded transition-colors border-0 bg-transparent"
                            style={{ background: 'transparent' }}
                            title="Edit Organizer"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1ecf1'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleStatusToggle(organizer.id, organizer.status)}
                            className={`p-2 ${organizer.status === 1 ? 'text-warning hover:bg-warning' : 'text-success'} rounded transition-colors border-0 bg-transparent`}
                            style={{ background: 'transparent' }}
                            title={organizer.status === 1 ? 'Deactivate' : 'Activate'}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = organizer.status === 1 ? '#fff3cd' : '#d1e7dd'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            {organizer.status === 1 ? (
                              <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(organizer.id)}
                            className="p-2 text-danger rounded transition-colors border-0 bg-transparent"
                            style={{ background: 'transparent' }}
                            title="Delete"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8d7da'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">No organizers found</td>
                  </tr>
                )}
              </tbody>
            </table>
              {renderPagination()}
          </div>
            )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingOrganizer && (
          <div
              className="modal fade show"
              style={{
                  display: "block",
                  overflowY: "auto", // ✔ allows whole-page scroll on mobile
                  backgroundColor: "rgba(0,0,0,0.5)"
              }}
              onClick={handleCloseModal}
          >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
              <div
                  className="modal-content"
                  style={{ maxHeight: '90vh', overflowY: 'auto' }}
              >
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-edit mr-2"></i>
                  Edit Organizer
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={handleCloseModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmitEdit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Events</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.max_events}
                      onChange={(e) => setFormData({ ...formData, max_events: e.target.value })}
                    />
                  </div>
                    <div className="form-group">
                        <label>Commission</label>
                        <input
                            type="text"
                            className="form-control"
                            value={formData.commission}
                            onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
                        />
                    </div>
                  <div className="form-group">
                    <label>Hotline</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.hotline}
                      onChange={(e) => setFormData({ ...formData, hotline: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="submit" className="btn btn-sm btn-success">
                    <i className="fas fa-save mr-2"></i>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

        {showCreateModal && (
            <div
                className="modal fade show"
                style={{
                    display: "block",
                    backgroundColor: "rgba(0,0,0,0.5)"
                }}
                onClick={() => setShowCreateModal(false)}
            >
                <div
                    className="modal-dialog modal-dialog-centered modal-lg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="fas fa-user-plus mr-2"></i>
                                Create Organizer
                            </h5>
                            <button className="close" onClick={() => setShowCreateModal(false)}>
                                <span>&times;</span>
                            </button>
                        </div>

                        <form onSubmit={handleCreateOrganizer}>
                            <div className="modal-body">

                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={createData.name}
                                        onChange={(e) =>
                                            setCreateData({ ...createData, name: e.target.value })
                                        }
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email *</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={createData.email}
                                        onChange={(e) =>
                                            setCreateData({ ...createData, email: e.target.value })
                                        }
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Max Events *</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={createData.max_events}
                                        onChange={(e) =>
                                            setCreateData({ ...createData, max_events: e.target.value })
                                        }
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Commission (%) *</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={createData.commission}
                                        onChange={(e) =>
                                            setCreateData({ ...createData, commission: e.target.value })
                                        }
                                        required
                                    />
                                </div>

                                <div className="alert alert-info mb-0">
                                    A random password will be generated and emailed to the organizer.
                                </div>

                            </div>

                            <div className="modal-footer">
                                <button type="submit" className="btn btn-sm btn-primary">
                                    Create Organizer
                                </button>
                            </div>
                        </form>

                    </div>
                </div>
            </div>
        )}

    </div>
  );
};

export default ManageOrganizers;

